import React, { useState, useCallback, useContext, useEffect } from 'react';

import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { Alert, Container, Snackbar } from '@mui/material';

import ToastContext from '../Context/ToastContext';

import { setApiErrorHandler } from '../Assets/Axios/Axios';

import BackToTop from '../Components/BackToTop';

import Home from '../Pages/Home';
import Category from '../Pages/Category';
import AllCategories from '../Pages/AllCategories';
import SearchResults from '../Pages/Search';
import Seller from '../Pages/Seller';

import Careers from '../Pages/Static/Careers';
import PressNews from '../Pages/Static/PressNews';
import Partnerships from '../Pages/Static/Partnerships';
import PrivacyPolicy from '../Pages/Static/PrivacyPolicy';
import TermsOfService from '../Pages/Static/TermsOfService';
import FirebaseToken from '../Pages/FirebaseToken';

//Professional Pages
import ProfessionalRegistration from '../Pages/Professional/Registration';
import RegisterProfessional from '../Pages/ProfessionalRegistration/ProfessionalRegistration';
import Login from '../Pages/ProfessionalLogin/Login';
import ForgotPassword from '../Pages/ProfessionalForgotPassword';
import ProfessionalStatus from '../Pages/ProfessionalStatus';

//Professional Dashboard
import ProfessionalMyDetails from '../Pages/ProfessionalPage/MyDetails';
import Service from '../Pages/ProfessionalPage/Service';
import Packages from '../Pages/ProfessionalPage/Packages';
import Password from '../Pages/ProfessionalPage/Password';
import ProfessionalJobRequests from '../Pages/ProfessionalPage/JobRequests';
import JobRequestsAdvanceSection from '../Pages/ProfessionalPage/Components/JobRequestsAdvanceSection';
import JobRequestsResponseSection from '../Pages/ProfessionalPage/Components/JobRequestsResponseSection.jsx';
import ProfessionalJobInformation from '../Pages/ProfessionalPage/JobInformation';
import JobInformationInCompleteSection from '../Pages/ProfessionalPage/Components/JobInformationInCompleteSection';
import JobInformationCompleteSection from '../Pages/ProfessionalPage/Components/JobInformationCompleteSection';
import ProfessionalReviewManage from '../Pages/ProfessionalPage/ReviewManage';

//User Dashboard
import MyDetails from '../Pages/UserPage/MyDetails';
import JobRequests from '../Pages/UserPage/JobRequests';
import JobInformation from '../Pages/UserPage/JobInformations';
import ReviewManage from '../Pages/UserPage/ReviewManage';

//Admin Dashboard
import AdminLogin from '../Pages/AdminPanel/Login';
import DashboardLayout from '../Pages/AdminPanel/Components/DashboardLayout';
import Dashboard from '../Pages/AdminPanel/Dashboard';
import Professionals from '../Pages/AdminPanel/Professionals';
import ProfessionalsRequest from '../Pages/AdminPanel/ProfessionalsRequest';
import BookingRequests from '../Pages/AdminPanel/BookingRequests';
import Client from '../Pages/AdminPanel/Client';
import JobManage from '../Pages/AdminPanel/JobManage';
import Transaction from '../Pages/AdminPanel/Transaction';
import Services from '../Pages/AdminPanel/Services';
import Categories from '../Pages/AdminPanel/Categories';
import CompanyInformation from '../Pages/AdminPanel/CompanyInformation';

import PageNotFound from '../Pages/PageNotFound';

import ProtectedRoute from '../Components/ProtectedRoute';
import ScrollTopPageChange from '../Components/ScrollTopPageChange';

import { USER_TYPES } from '../Constant/Enums';

import AuthContext from '../Context/AuthContext';

function HomeNavigation() {
  const { user, isAdminLogged } = useContext(AuthContext);

  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');

  const showToastMessage = useCallback((message, type = 'error') => {
    setIsToastOpen(true);
    setToastMessage(message);
    setToastType(type);
  }, []);

  // Surface any failed API request (network / HTTP error) as a toast,
  // so silent failures from un-caught Axios rejections are never lost.
  useEffect(() => {
    setApiErrorHandler(showToastMessage);
    return () => setApiErrorHandler(null);
  }, [showToastMessage]);

  const isProfessionalLogged = user?.type === USER_TYPES.PROFESSIONAL;
  const isEventBookerLogged = user?.type === USER_TYPES.USER;

  return (
    <ToastContext.Provider value={{ showToastMessage }}>
      <Container maxWidth='xl' disableGutters>
        <BrowserRouter>
          <ScrollTopPageChange>
            <Routes>
              <Route path='admin/login' element={<AdminLogin />} />
              
              <Route
                path='admin'
                element={
                  <ProtectedRoute condition={isAdminLogged} redirectTo='/admin/login'>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route path='dashboard' element={<Dashboard />} />
                <Route path='professionals' element={<Professionals />} />
                <Route path='professionals-request' element={<ProfessionalsRequest />} />
                <Route path='booking-request' element={<BookingRequests />} />
                <Route path='client' element={<Client />} />
                <Route path='job-manage' element={<JobManage />} />
                <Route path='transaction' element={<Transaction />} />
                <Route path='services' element={<Services />} />
                <Route path='categories' element={<Categories />} />
                <Route path='manage' element={<CompanyInformation />} />
              </Route>

              {isEventBookerLogged && (
                <Route path='user/dashboard'>
                  <Route path='my-details' element={<MyDetails />} />
                  <Route path='job-requests' element={<JobRequests />} />
                  <Route path='review-manage' element={<ReviewManage />} />
                  <Route path='job-information' element={<JobInformation />} />
                </Route>
              )}

              <Route path='professional'>
                <Route path='status' element={<ProfessionalStatus />} />
                {!isProfessionalLogged && (
                  <>
                    <Route index element={<RegisterProfessional />} />
                    <Route path='registration' element={<ProfessionalRegistration />} />
                    <Route path='login'>
                      <Route index element={<Login />} />
                      <Route path='forgot-password' element={<ForgotPassword />} />
                    </Route>
                  </>
                )}

                {isProfessionalLogged && (
                  <Route path='dashboard'>
                    <Route path='my-details' element={<ProfessionalMyDetails />} />
                    <Route path='service' element={<Service />} />
                    <Route path='packages' element={<Packages />} />
                    <Route path='review-manage' element={<ProfessionalReviewManage />} />
                    <Route path='password' element={<Password />} />

                    <Route path='job-requests' element={<ProfessionalJobRequests />}>
                      <Route path='response' element={<JobRequestsResponseSection />} />
                      <Route path='advance' element={<JobRequestsAdvanceSection />} />
                    </Route>

                    <Route path='job-information' element={<ProfessionalJobInformation />}>
                      <Route path='in-complete' element={<JobInformationInCompleteSection />} />
                      <Route path='complete' element={<JobInformationCompleteSection />} />
                    </Route>
                  </Route>
                )}
              </Route>

              <Route path='categories' element={<AllCategories />} />
              <Route path='search' element={<SearchResults />} />
              <Route path='category/:slug' element={<Category />} />

              <Route path='careers' element={<Careers />} />
              <Route path='press' element={<PressNews />} />
              <Route path='partnerships' element={<Partnerships />} />
              <Route path='privacy-policy' element={<PrivacyPolicy />} />
              <Route path='terms-of-service' element={<TermsOfService />} />
              <Route path='professional/:serviceId' element={<Seller />} />
              <Route path='token' element={<FirebaseToken />} />
              
              <Route index element={<Home />} />
              <Route path='*' element={<PageNotFound />} />
            </Routes>
          </ScrollTopPageChange>
        </BrowserRouter>

        <BackToTop />
        <Snackbar
          autoHideDuration={2000}
          open={isToastOpen}
          onClose={() => setIsToastOpen(false)}
          translate='yes'
        >
          <Alert severity={toastType}>{toastMessage}</Alert>
        </Snackbar>
      </Container>
    </ToastContext.Provider>
  );
}

export default HomeNavigation;
